import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { hymotsLogsSocketHelper } from 'src/app/helpers';
import { generator } from 'src/app/helpers/map-unit-generator.helper';

import { contentActions, unitsActions } from 'src/app/redux/actions';
import {
  EDugumNotificaions,
  EEmirNotifications,
  EEtmenNotifications,
  EEtmenTuruNotifications,
} from 'src/enums/notifications.enum';

import { reduxRootTypes } from 'src/types';
import { DugumResultType } from 'src/types/services/hymotsc/dugum.type';
import { EmirResultType } from 'src/types/services/hymotsc/emir.type';
import { EtmenTurleriResultType } from 'src/types/services/hymotsc/etmen-turleri.type';
import { EtmenResultType } from 'src/types/services/hymotsc/etmen.type';

const WBConnectionHandler = () => {
  const dispatch = useDispatch();

  const { hymotsDB } = useSelector((state: reduxRootTypes.RootStateType) => state.eventState);
  const { hymotseStarted } = useSelector((state: reduxRootTypes.RootStateType) => state.socketState);

  const { agents, prototypes, factions, sides, obstacles } = useSelector(
    (state: reduxRootTypes.RootStateType) => state.contentState,
  );

  const createEtmenByNotification = ({
    etmen,
    etmenTuru,
  }: {
    etmen: EtmenResultType;
    etmenTuru?: EtmenTurleriResultType;
  }) => {
    const newPrototypes = etmenTuru ? [...prototypes, etmenTuru] : prototypes;

    let allControllingUnitIds: number[] = [];

    const mapUnit = generator(etmen, newPrototypes, factions, sides, obstacles, allControllingUnitIds);

    if (mapUnit) {
      dispatch(unitsActions.addUnit(mapUnit));
    }
    dispatch(contentActions.setPrototypes(newPrototypes));
  };

  const createEtmenTuruByNotification = ({ etmenTuru }: { etmenTuru?: EtmenTurleriResultType }) => {
    const newPrototypes = etmenTuru ? [...prototypes, etmenTuru] : prototypes;
    dispatch(contentActions.setPrototypes(newPrototypes));
  };

  const onDeleteAgentsCreateAtSimulation = (ids: number[]) => {
    dispatch(unitsActions.deleteUnits(ids));
  };

  const onDeletePrototypesCreateAtSimulation = (ids: number[]) => {
    dispatch(contentActions.removePrototypes(ids));
  };

  const createCommandNotification = (payload: EmirResultType) => {
    dispatch(contentActions.addCommand(payload));
  };

  const updateCommandNotification = (payload: EmirResultType) => {
    dispatch(contentActions.editCommand(payload));
  };

  const deleteCommandsNotification = (ids: number[]) => {
    dispatch(contentActions.deleteCommands(ids));
  };

  const createNodeByNotification = (payload: DugumResultType) => {
    dispatch(contentActions.addNode(payload));
  };

  const deleteNodeByNotification = (ids: number[]) => {
    dispatch(contentActions.deleteNodes(ids));
  };

  const createLogSocketConnection = (runIdParam?: string) => {
    hymotsLogsSocketHelper.createConnection();

    hymotsLogsSocketHelper.listenSocket(`${EEtmenNotifications.ADD}-${hymotsDB}`, createEtmenByNotification);
    hymotsLogsSocketHelper.listenSocket(
      `${EEtmenNotifications.BULK_DELETE}-${hymotsDB}`,
      onDeleteAgentsCreateAtSimulation,
    );

    hymotsLogsSocketHelper.listenSocket(`${EEtmenTuruNotifications.ADD}-${hymotsDB}`, createEtmenTuruByNotification);
    hymotsLogsSocketHelper.listenSocket(
      `${EEtmenTuruNotifications.BULK_DELETE}-${hymotsDB}`,
      onDeletePrototypesCreateAtSimulation,
    );

    hymotsLogsSocketHelper.listenSocket(`${EEmirNotifications.CREATE}_${hymotsDB}`, createCommandNotification);
    hymotsLogsSocketHelper.listenSocket(`${EEmirNotifications.UPDATE}_${hymotsDB}`, updateCommandNotification);
    hymotsLogsSocketHelper.listenSocket(`${EEmirNotifications.DELETE}_${hymotsDB}`, deleteCommandsNotification);

    hymotsLogsSocketHelper.listenSocket(`${EDugumNotificaions.ADD}_${hymotsDB}`, createNodeByNotification);
    hymotsLogsSocketHelper.listenSocket(`${EDugumNotificaions.BULK_DELETE}_${hymotsDB}`, deleteNodeByNotification);
  };

  useEffect(() => {
    try {
      if (!agents) {
        console.warn('Agents are not loaded.');
      }
      if (!prototypes) {
        console.warn('Prototypes are not loaded.');
      }
      if (!factions) {
        console.warn('Factions are not loaded.');
      }
      if (!sides) {
        console.warn('Sides are not loaded.');
      }

      const areAllDependenciesLoaded = agents?.length && prototypes?.length && factions?.length && sides?.length;

      if (areAllDependenciesLoaded && !hymotseStarted) {
        createLogSocketConnection();
      } else if (hymotseStarted) {
        hymotsLogsSocketHelper.closeSocket();
      }
    } catch (error) {
      console.error('An error occurred while managing the socket connection:', error);
    }
  }, [agents, prototypes, factions, sides, hymotseStarted]);

  return null;
};

export default WBConnectionHandler;
